import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { IMAGES, PHONE, PHONE_LINK } from "../data";

const LINES = [
  { text: "Nature,", accent: false },
  { text: "freshly", accent: true },
  { text: "squeezed.", accent: false },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 120, damping: 18 });

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={sectionRef} id="top" className="relative overflow-hidden pt-32 md:pt-40">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-end gap-12 px-6 md:px-10 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="mb-6 font-cursive text-2xl text-melon md:text-3xl"
          >
            cold-pressed, small batch, juiced with love
          </motion.p>

          <h1 className="font-serif font-medium leading-[0.88] tracking-tighter text-ink">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}
                  className={`block text-[clamp(4rem,11.5vw,10.5rem)] ${
                    line.accent ? "font-cursive font-bold text-melon" : ""
                  }`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-md text-base font-light leading-relaxed text-leaf/80 md:text-lg"
          >
            Cold-pressed juices, wellness shots and juice cleanses made from real
            fruit and roots. No additives. No preservatives. No added sugar.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#menu"
              data-testid="hero-explore-menu-button"
              className="group flex items-center gap-3 rounded-full bg-leaf px-8 py-4 text-sm font-medium tracking-wide text-cream transition-[background-color,transform] duration-300 hover:scale-95 hover:bg-melon"
            >
              Explore the menu
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
            <a
              href={PHONE_LINK}
              data-testid="hero-call-button"
              className="flex items-center gap-3 rounded-full border border-leaf/30 px-8 py-4 text-sm font-medium tracking-wide text-leaf transition-[border-color,color,transform] duration-300 hover:scale-95 hover:border-melon hover:text-melon"
            >
              <Phone className="h-4 w-4" />
              {PHONE}
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-t-[999px] rounded-b-[32px] border border-leaf/15">
              <motion.img
                src={IMAGES.hero}
                alt="Fresh watermelon mint juice"
                data-testid="hero-image"
                style={{ y: imgY, scale: 1.15 }}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
              className="absolute -left-10 top-8 hidden h-32 w-32 items-center justify-center md:flex"
            >
              <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow">
                <defs>
                  <path id="badge-circle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                </defs>
                <text className="fill-leaf text-[9.5px] font-medium uppercase tracking-[0.22em]">
                  <textPath href="#badge-circle">Freshly made • Natural ingredients •</textPath>
                </text>
              </svg>
              <div className="absolute inset-0 m-auto h-3 w-3 rounded-full bg-melon" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="mx-auto mt-16 grid max-w-[1440px] grid-cols-3 border-t border-leaf/15 px-6 md:mt-24 md:px-10"
      >
        {[
          ["12", "signature blends"],
          ["0g", "added sugar"],
          ["100%", "real ingredients"],
        ].map(([big, small], i) => (
          <div key={small} className={`py-8 ${i > 0 ? "border-l border-leaf/15 pl-6 md:pl-10" : ""}`}>
            <p className="font-serif text-3xl font-medium text-leaf md:text-5xl">{big}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-leaf/60">{small}</p>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
